import { useEffect, useRef, useState } from "react";
import { useScroll } from "framer-motion";
import {
  getPortfolioScrollScenes,
  PORTFOLIO_SCROLL_VISUAL,
} from "@/data/portfolioScrollSections";
import { formatParagraphLines } from "@/pages/portfolio/formatParagraphLines";
import "./PortfolioDetailScrollSections.css";

function getSceneHeadline(scene) {
  return scene.headline || scene.label;
}

function isHighlighted(index, activeIndex, sceneCount) {
  if (index === activeIndex) return true;
  if (activeIndex >= sceneCount - 1) return false;
  return index === activeIndex + 1;
}

function SceneNavBody({ scene }) {
  if (scene.type === "features") {
    return (
      <ul className="ds-pf-detail-scroll__nav-features font-kr">
        {scene.items.map((item) => (
          <li key={item} className="ds-pf-detail-scroll__nav-feature">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (!scene.body) return null;

  return (
    <p className="ds-pf-detail-scroll__nav-body font-kr">{scene.body}</p>
  );
}

function DetailLines({ lines, className }) {
  return (
    <div className={className}>
      {lines.map((line, index) => (
        <span key={`${line}-${index}`} className="ds-pf-detail-scroll__line">
          {line}
        </span>
      ))}
    </div>
  );
}

function buildFallbackScenes(project) {
  return [
    { id: "overview", label: "Overview", type: "text", headline: "Overview", body: project.overview },
    { id: "problem", label: "Problem", type: "text", headline: "Problem", body: project.problem },
    { id: "solution", label: "Solution", type: "text", headline: "Solution", body: project.solution },
    {
      id: "features",
      label: "Key Features",
      type: "features",
      headline: "Key Features",
      items: project.keyFeatures.slice(0, 4),
    },
    { id: "ui-concept", label: "UI Concept", type: "text", headline: "UI Concept", body: project.uiConcept },
    {
      id: "expected-result",
      label: "Expected Result",
      type: "text",
      headline: "Expected Result",
      body: project.expectedResult,
    },
  ];
}

function buildScenes(project) {
  return getPortfolioScrollScenes(project.slug) ?? buildFallbackScenes(project);
}

function DetailStaticSections({ project }) {
  const scenes = buildFallbackScenes(project);
  return (
    <div className="ds-portfolio-detail__sections ds-pf-detail-scroll__static">
      {scenes.map((scene) =>
        scene.type === "features" ? (
          <article key={scene.id} className="ds-portfolio-detail__section">
            <h2 className="ds-portfolio-detail__section-title">{scene.label}</h2>
            <ul className="ds-portfolio-detail__features">
              {scene.items.map((item) => (
                <li key={item} className="ds-portfolio-detail__feature">
                  {item}
                </li>
              ))}
            </ul>
          </article>
        ) : (
          <article key={scene.id} className="ds-portfolio-detail__section">
            <h2 className="ds-portfolio-detail__section-title">{scene.label}</h2>
            <DetailLines
              lines={formatParagraphLines(scene.body || "")}
              className="ds-portfolio-detail__section-body"
            />
          </article>
        ),
      )}
    </div>
  );
}

function DetailScrollSequence({ project }) {
  const scenes = buildScenes(project);
  const sceneCount = scenes.length;
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const idx = Math.min(sceneCount - 1, Math.max(0, Math.floor(v * sceneCount)));
      setActiveIndex(idx);
    });
  }, [scrollYProgress, sceneCount]);

  return (
    <div
      ref={containerRef}
      className="ds-pf-detail-scroll"
      data-testid="portfolio-detail-scroll-sections"
      style={{ height: `${sceneCount * 100}svh` }}
    >
      <div className="ds-pf-detail-scroll__sticky">
        <div className="ds-pf-detail-scroll__grid" aria-hidden="true" />

        <div className="ds-pf-detail-scroll__stage">
          <div className="ds-pf-detail-scroll__layout">
            <nav
              className="ds-pf-detail-scroll__nav"
              aria-label="프로젝트 상세"
              data-testid="portfolio-detail-scroll-nav"
            >
              {scenes.map((scene, index) => {
                const isActive = index === activeIndex;
                const highlighted = isHighlighted(index, activeIndex, sceneCount);
                const headline = getSceneHeadline(scene);

                return (
                  <div
                    key={scene.id}
                    data-testid={`portfolio-detail-scene-${scene.id}`}
                    className={`ds-pf-detail-scroll__nav-item${highlighted ? " is-highlighted" : ""}${isActive ? " is-active" : ""}`}
                  >
                    <span className="ds-pf-detail-scroll__nav-indicator" aria-hidden="true" />
                    <div className="ds-pf-detail-scroll__nav-inner">
                      {highlighted ? (
                        <>
                          <span className="ds-pf-detail-scroll__nav-label">{scene.label}</span>
                          <h3 className="ds-pf-detail-scroll__nav-title font-kr">{headline}</h3>
                          {isActive ? <SceneNavBody scene={scene} /> : null}
                        </>
                      ) : (
                        <p className="ds-pf-detail-scroll__nav-title font-kr">{headline}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </nav>

            <div className="ds-pf-detail-scroll__visual">
              <div className="ds-pf-detail-scroll__visual-frame">
                <img
                  src={PORTFOLIO_SCROLL_VISUAL}
                  alt="DSiteSolution brand visual"
                  className="ds-pf-detail-scroll__visual-image"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          <div className="ds-pf-detail-scroll__progress" aria-hidden="true">
            {scenes.map((scene, i) => (
              <span
                key={scene.id}
                className={`ds-pf-detail-scroll__dot${i === activeIndex ? " is-active" : ""}`}
              />
            ))}
          </div>

          <div className="ds-pf-detail-scroll__counter font-display">
            {String(activeIndex + 1).padStart(2, "0")}
            <span className="ds-pf-detail-scroll__counter-muted"> / {String(sceneCount).padStart(2, "0")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PortfolioDetailScrollSections({ project }) {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  if (reduceMotion) {
    return <DetailStaticSections project={project} />;
  }

  return <DetailScrollSequence project={project} />;
}
